import { useEffect, useState } from "react";
import { api } from "../api/client";
import { commsTypeLabel } from "../lib/commsTypeLabels";

// The per-user comms timeline (spec-6 ac-1/ac-2/ac-3): every communication to one
// human across all channels, scheduled items pinned at the top, then sent
// newest-first. Read-only — it renders what core recorded in comms_log. Reused as
// a section on the user drill-down (UserDetailPage).

export interface CommsEntry {
  id: string;
  channel: string;
  type: string;
  status: string;
  scheduledFor: string | null;
  sentAt: string | null;
  subject: string | null;
  createdAt: string;
}

const CHANNEL_LABEL: Record<string, string> = {
  email: "Email",
  in_app: "In-app",
  badge: "Badge",
  os: "OS",
};

function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel;
}

function whenLabel(e: CommsEntry): string {
  if (e.status === "scheduled" && e.scheduledFor) {
    return `Scheduled for ${new Date(e.scheduledFor).toLocaleString()}`;
  }
  const ts = e.sentAt ?? e.createdAt;
  return new Date(ts).toLocaleString();
}

export function CommsTimeline({ userId }: { userId: string }) {
  const [entries, setEntries] = useState<CommsEntry[] | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");

  useEffect(() => {
    if (!userId) return;
    api<{ timeline: CommsEntry[] }>(`/api/comms/users/${userId}`)
      .then((r) => {
        setEntries(r.timeline);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, [userId]);

  if (status === "loading") return <p className="px-4 py-3 text-sm text-muted">Loading…</p>;
  if (status === "error")
    return <p className="px-4 py-3 text-sm text-status-danger-text">Failed to load communications.</p>;
  if (!entries || entries.length === 0)
    return <p className="px-4 py-3 text-sm text-muted">No communications yet.</p>;

  return (
    <ul data-testid="comms-timeline">
      {entries.map((e) => {
        const scheduled = e.status === "scheduled";
        return (
          <li
            key={e.id}
            data-testid="comms-entry"
            data-status={e.status}
            className="flex items-center justify-between gap-4 border-b border-edge-subtle px-4 py-2.5 last:border-0"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="rounded-md bg-btn-secondary px-1.5 py-0.5 text-xs text-secondary">
                  {channelLabel(e.channel)}
                </span>
                <span className="truncate text-sm font-medium text-primary">
                  {e.subject ?? commsTypeLabel(e.type)}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted">
                {commsTypeLabel(e.type)} · {whenLabel(e)}
              </p>
            </div>
            <span
              className={`flex-none text-xs ${
                scheduled
                  ? "font-medium text-primary"
                  : e.status === "failed"
                    ? "text-status-danger-text"
                    : "text-muted"
              }`}
            >
              {e.status}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
